import httpClient from '../../../api/httpClient';
import API_CONFIG from '../../../config/api.config';
import { 
  Patient, 
  PatientListResponse, 
  PatientFilterParams,
  PatientFormData
} from '../types/patient.types';

export const patientApi = {
  /**
   * Get paginated list of patients
   */
  getPatients: async (params: PatientFilterParams): Promise<PatientListResponse> => {
    const response = await httpClient.get<PatientListResponse>(API_CONFIG.ENDPOINTS.PATIENTS, { params });
    return response.data;
  },

  /**
   * Get a single patient by ID
   */
  getPatientById: async (id: string): Promise<Patient> => {
    const response = await httpClient.get<Patient>(`${API_CONFIG.ENDPOINTS.PATIENTS}/${id}`);
    return response.data;
  },

  /**
   * Create a new patient
   */
  createPatient: async (data: Omit<PatientFormData, 'mrn'>): Promise<Patient> => {
    try {
      console.log('Sending patient data:', JSON.stringify(data, null, 2));
      
      // Log the full request details
      console.log('Request config:', {
        url: `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PATIENTS}`,
        method: 'POST',
        data: data
      });
      
      const response = await httpClient.post<Patient>(
        API_CONFIG.ENDPOINTS.PATIENTS, 
        data,
        {
          // Add this to see the full response even on error
          validateStatus: (status) => status < 500
        }
      );
      
      // If we get here but status is not 2xx, it's a validation error
      if (response.status >= 400) {
        console.error('Validation error from server:', response.data);
        const errorMessage = (response.data as any)?.message || 'Validation failed';
        throw new Error(errorMessage);
      }
      
      return response.data;
    } catch (error: any) {
      const errorDetails = {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          data: error.config?.data,
          headers: error.config?.headers
        },
        stack: error.stack
      };
      
      console.error('Error creating patient:', JSON.stringify(errorDetails, null, 2));
      
      // Re-throw with more context
      const errorMessage = error.response?.data?.message || 
                         error.response?.data?.error || 
                         error.message || 
                         'Failed to create patient';
      const validationErrors = error.response?.data?.errors || {};
      
      const enhancedError = new Error(errorMessage);
      enhancedError.name = error.name || 'PatientCreationError';
      (enhancedError as any).status = error.response?.status;
      (enhancedError as any).validationErrors = validationErrors;
      
      throw enhancedError;
    }
  },

  /**
   * Update an existing patient
   */
  updatePatient: async (id: string, data: PatientFormData): Promise<Patient> => {
    try {
      console.log('Updating patient with data:', JSON.stringify(data, null, 2));
      
      // Define read-only fields that should not be included in the update
      const readOnlyFields = ['id', 'createdAt', 'updatedAt', 'createdBy', 'updatedBy'];
      
      // Define optional fields that can be set to null
      const optionalFields = ['phoneNumber', 'email', 'address', 'note'];
      
      // Create a new object with only defined and allowed fields
      const updateData: Record<string, any> = {};
      
      // Process each field in the form data
      (Object.keys(data) as Array<keyof PatientFormData>).forEach(key => {
        const value = data[key];
        const keyStr = key as string;
        
        // Skip read-only fields
        if (readOnlyFields.includes(keyStr)) {
          return;
        }
        
        // For optional fields, include them even if empty (to clear them)
        if (optionalFields.includes(keyStr)) {
          updateData[keyStr] = value === '' ? null : value;
        }
        // For required fields, only include if they have a value
        else if (value !== undefined && value !== '') {
          updateData[keyStr] = value;
        }
      });
      
      console.log('Processed update data:', JSON.stringify(updateData, null, 2));
      
      console.log('Sending update data:', JSON.stringify(updateData, null, 2));
      
      const response = await httpClient.patch<Patient>(
        `${API_CONFIG.ENDPOINTS.PATIENTS}/${id}`,
        updateData,
        {
          headers: {
            'Content-Type': 'application/json',
          },
          validateStatus: (status) => status < 500
        }
      );
      
      if (response.status >= 400) {
        console.error('Update validation error:', response.data);
        const errorMessage = (response.data as any)?.message || 'Update validation failed';
        throw new Error(errorMessage);
      }
      
      return response.data;
    } catch (error: any) {
      const errorDetails = {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          data: error.config?.data,
          headers: error.config?.headers
        },
        stack: error.stack
      };
      
      console.error('Error updating patient:', JSON.stringify(errorDetails, null, 2));
      
      // Re-throw with more context
      const errorMessage = error.response?.data?.message || 
                         error.response?.data?.error || 
                         error.message || 
                         'Failed to update patient';
      const validationErrors = error.response?.data?.errors || {};
      
      const enhancedError = new Error(errorMessage);
      enhancedError.name = error.name || 'PatientUpdateError';
      (enhancedError as any).status = error.response?.status;
      (enhancedError as any).validationErrors = validationErrors;
      
      throw enhancedError;
    }
  },

  /**
   * Delete a patient (soft delete)
   */
  deletePatient: async (id: string): Promise<void> => {
    await httpClient.delete(`${API_CONFIG.ENDPOINTS.PATIENTS}/${id}`);
  },

  /**
   * Toggle patient active status
   */
  togglePatientStatus: async (id: string, isActive: boolean): Promise<Patient> => {
    const response = await httpClient.patch<Patient>(`${API_CONFIG.ENDPOINTS.PATIENTS}/${id}/status`, { isActive });
    return response.data;
  },

  /**
   * Search patients by name or MRN (returns all matching patients without pagination)
   */
  searchPatients: async (query: string): Promise<Patient[]> => {
    const response = await httpClient.get<PatientListResponse>(API_CONFIG.ENDPOINTS.PATIENTS, {
      params: { 
        search: query,
        limit: 1000, // High limit to get all matching results
        page: 1
      },
    });
    return response.data.data; // Extract the data array from paginated response
  },

  /**
   * Check if MRN already exists
   */
  checkMrnExists: async (mrn: string): Promise<boolean> => {
    try {
      await httpClient.get(`${API_CONFIG.ENDPOINTS.PATIENTS}/mrn/${mrn}/exists`);
      return true;
    } catch (error: unknown) {
      // Check if error is an Axios error
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { status?: number } };
        if (axiosError.response?.status === 404) {
          return false;
        }
      }
      throw error;
    }
  },
  /**
   * Check if phone number already exists
   */
  checkPhoneExists: async (phoneNumber: string): Promise<Patient[]> => {
    try {
      const response = await httpClient.get<Patient[]>(`${API_CONFIG.ENDPOINTS.PATIENTS}/phone/${phoneNumber}/exists`);
      return response.data;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { status?: number } };
        if (axiosError.response?.status === 404) {
          return [];
        }
      }
      throw error;
    }
  },

  /**
   * Bulk import patients from CSV
   */
  importPatients: async (patients: any[]): Promise<{ imported: number; failed: number; errors: any[] }> => {
    const response = await httpClient.post('/api/patients/import', { patients });
    return response.data;
  },
};

export default patientApi;
